/**
 * Inbox Service
 * 
 * Serviço para gerenciar múltiplos inboxes WhatsApp configurados dinamicamente.
 */

import { api } from './api';

export interface DynamicInboxConfig {
  id: string;
  inboxNumber: number;
  type: 'onboarding' | 'recovery';
  name: string;
  provider: 'zapi' | 'cloud_api' | 'evolution_api';
  instanceId?: string;
  instanceName?: string;
  n8nWebhookUrl?: string;
  channelId?: string;
  isDefault: boolean;
  isActive: boolean;
  path: string;
  channelType: string;
  unitId?: string | null;
}

export interface InboxMenuItem {
  id: string;
  text: string;
  path: string;
  type: 'onboarding' | 'recovery';
  provider: string;
  isDefault: boolean;
  channelId?: string;
  channelType: string;
  instanceId?: string;
  instanceName?: string;
  unitId?: string | null;
  purpose?: 'platform_support' | null;
  icon: string;
  badge: string | null;
}

export interface InboxListResponse {
  defaultOnboarding: DynamicInboxConfig;
  defaultRecovery: DynamicInboxConfig;
  additionalInboxes: DynamicInboxConfig[];
  allInboxes: DynamicInboxConfig[];
}

export interface InboxUnreadCountsResponse {
  byChannel: Record<string, number>;
  centralTotal: number;
  supportTotal?: number;
}

export interface InboxMenuResponse {
  all: InboxMenuItem[];
  onboarding: InboxMenuItem[];
  recovery: InboxMenuItem[];
}

class InboxService {
  private cache: InboxMenuResponse | null = null;
  private cacheTimestamp: number | null = null;
  private cacheExpiryMs = 5 * 60 * 1000; // 5 minutos

  /**
   * Busca todos os inboxes configurados
   */
  async getAllInboxes(forceRefresh = false): Promise<InboxListResponse> {
    try {
      const response = await api.get(`/inboxes${forceRefresh ? '?refresh=true' : ''}`);
      return response.data.data;
    } catch (error) {
      console.error('Erro ao buscar inboxes:', error);
      throw error;
    }
  }

  /**
   * Busca inboxes formatados para o menu lateral
   */
  async getMenuInboxes(forceRefresh = false): Promise<InboxMenuResponse> {
    try {
      // Verificar cache
      if (
        !forceRefresh && 
        this.cache && 
        this.cacheTimestamp && 
        (Date.now() - this.cacheTimestamp) < this.cacheExpiryMs
      ) {
        return this.cache;
      }

      // Cache-bust para evitar 304 do navegador entre trocas de unidade
      const response = await api.get('/inboxes/menu', {
        params: { _ts: Date.now() },
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
      });
      this.cache = response.data.data;
      this.cacheTimestamp = Date.now();
      return response.data.data;
    } catch (error) {
      console.error('Erro ao buscar menu de inboxes:', error);
      
      // Retornar dados padrão em caso de erro
      return this.getDefaultMenuItems();
    }
  }

  /**
   * Retorna itens de menu padrão (fallback)
   */
  private getDefaultMenuItems(): InboxMenuResponse {
    const onboardingItem: InboxMenuItem = {
      id: 'default-onboarding',
      text: 'WhatsApp Onboarding',
      path: '/inbox/whatsapp',
      type: 'onboarding',
      provider: 'evolution_api',
      isDefault: true,
      channelType: 'whatsapp',
      icon: 'whatsapp',
      badge: null,
    };
    return {
      all: [onboardingItem],
      onboarding: [onboardingItem],
      recovery: [],
    };
  }

  /**
   * Agrega não lidas por canal (soma unread_count das conversas) + total da Central de Mensagens.
   */
  async getUnreadCounts(opts?: { purpose?: 'platform_support' }): Promise<InboxUnreadCountsResponse> {
    const response = await api.get('/inboxes/unread-counts', {
      params: {
        _ts: Date.now(),
        ...(opts?.purpose ? { purpose: opts.purpose } : {}),
      },
      headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
    });
    return response.data.data;
  }

  /**
   * Busca inbox por path
   */
  async getInboxByPath(path: string): Promise<DynamicInboxConfig | null> {
    try {
      const response = await api.get(`/inboxes/by-path?path=${encodeURIComponent(path)}`);
      return response.data.data;
    } catch (error) {
      console.error('Erro ao buscar inbox por path:', error);
      return null;
    }
  }

  /**
   * Busca inbox por ID do canal
   */
  async getInboxByChannelId(channelId: string): Promise<DynamicInboxConfig | null> {
    try {
      const response = await api.get(`/inboxes/by-channel/${channelId}`);
      return response.data.data;
    } catch (error) {
      console.error('Erro ao buscar inbox por canal:', error);
      return null;
    }
  }

  /** Inbox dinâmico da conversa mais recente do contato (CRM → Abrir conversa). */
  async resolveInboxForContact(contactId: string): Promise<{
    path: string;
    conversationId: string | null;
    channelId: string | null;
    inboxName: string;
  } | null> {
    try {
      const response = await api.get(`/inboxes/for-contact/${contactId}`);
      return response.data.data;
    } catch (error) {
      console.error('Erro ao resolver inbox do contato:', error);
      return null;
    }
  }

  /**
   * Busca conversas de um inbox específico
   */
  async getConversations(inboxPath: string, page = 1, limit = 50): Promise<any> {
    try {
      // Extrair apenas a parte final do path (ex: 'whatsapp-vendas' de '/inbox/whatsapp-vendas')
      const pathParts = inboxPath.split('/');
      const inboxSlug = pathParts[pathParts.length - 1];
      
      const response = await api.get(`/inboxes/conversations/${inboxSlug}?page=${page}&limit=${limit}`);
      return response.data.data;
    } catch (error) {
      console.error('Erro ao buscar conversas do inbox:', error);
      throw error;
    }
  }

  /**
   * Sincroniza canais com o banco de dados
   */
  async syncChannels(): Promise<void> {
    try {
      await api.post('/inboxes/sync');
      this.invalidateCache();
    } catch (error) {
      console.error('Erro ao sincronizar canais:', error);
      throw error;
    }
  }

  /**
   * Invalida o cache local
   */
  invalidateCache(): void {
    this.cache = null;
    this.cacheTimestamp = null;
  }
}

export const inboxService = new InboxService();
export default inboxService;


